import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { Drawer } from '@openx/components-core/lib/Drawer/Drawer';
import { DrawerActionBar } from '@openx/components-core/lib/Drawer/DrawerActionBar';
import { DrawerHeader } from '@openx/components-core/lib/Drawer/DrawerHeader';
import { Loader } from '@openx/components-core/lib/Loader/Loader';
import { FlatList } from '@openx/components-core/lib/TransferList/FlatList';
import type { ListData, TypeFullAPI } from '@openx/types/inventoryList';
import { type UrlTargeting, UrlTargetingType } from '@openx/types/targeting/domains';
import { noop } from '@openx/utils/noop';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingContext } from '../../../utils/context/TargetingContext';
import { FiltersTitle } from '../FiltersTitle/FiltersTitle';
import { SelectedItems } from '../SelectedItems/SelectedItems';

export type InventoryListItemsProps = {
  type?: UrlTargetingType;
  onChange?: (newData: UrlTargeting | ((prevState: UrlTargeting) => UrlTargeting)) => void;
  readonly?: boolean;
  containerStyles?: SxProps<Theme>;
  inventorylistId?: string | null;
  setInventoryListItems?: (values: string[]) => void;
  listType: TypeFullAPI;
  accountUid: string;
  hideTitle?: boolean;
  enableRemoveOneItem?: boolean;
  disabledReasonMessage?: string;
};

export const InventoryListItems = ({
  onChange = noop,
  type = UrlTargetingType.whitelist,
  readonly,
  containerStyles = {},
  inventorylistId = '',
  listType,
  setInventoryListItems = noop,
  hideTitle = false,
  enableRemoveOneItem = false,
  disabledReasonMessage,
}: InventoryListItemsProps) => {
  const { useInventoryListByIdFetch } = useTargetingContext();
  const { inventoryList, loading } = useInventoryListByIdFetch(inventorylistId);

  const inventoryListDisplayValue = inventoryList?.name || inventorylistId || '';

  // Local state to track user-filtered items separately from parent state
  const [localFilteredItems, setLocalFilteredItems] = useState<string[]>([]);

  useEffect(() => {
    if (!inventorylistId) {
      setInventoryListItems([]);
      setLocalFilteredItems([]);
    }

    if (inventoryList?.values && inventorylistId) {
      setInventoryListItems(inventoryList.values);
      setLocalFilteredItems(inventoryList.values); // Initialize local filtered state
    }
  }, [inventoryList?.values, setInventoryListItems, inventorylistId]);

  const { t } = useTranslation();
  const [drawerOpen, setDrawerState] = useState(false);

  const allowedType = type !== UrlTargetingType.blacklist;

  const listData: ListData[] = useMemo(() => {
    return (
      inventoryList?.values.map(item => ({
        id: item,
        name: item,
        isOnList: true,
      })) || []
    );
  }, [inventoryList]);

  const handleCloseDrawer = useCallback(() => {
    setDrawerState(false);
  }, []);

  const handleChange = useCallback(
    (values: string[]) => {
      if (values.length === 0) {
        onChange(prevValues => ({
          type: prevValues.type,
          domain_targeting_option: prevValues.domain_targeting_option,
          urls: prevValues.urls,
        }));
        setLocalFilteredItems([]);
      } else {
        setLocalFilteredItems(values);
        setInventoryListItems(values);
      }
    },
    [onChange, setInventoryListItems]
  );

  if (loading) {
    return <Loader />;
  }

  if (!inventoryList) {
    return null;
  }

  return (
    <Box sx={containerStyles} data-test="inventory-list-items-section">
      <SelectedItems
        filtersTitle={t('{type} {listType} from inventory list [ {count} ]', {
          type: allowedType ? t('allowed') : t('blocked'),
          count: localFilteredItems?.length || 0,
          listType: listType === 'inventorylist.bundle' ? t('App Bundle ID') : t('domains'),
          disabledReasonMessage: disabledReasonMessage,
        })}
        selectedItems={localFilteredItems}
        isAddIcon={allowedType}
        customDisplayLimit={5}
        isRemovingAll={!readonly}
        enableRemoveOneItem={enableRemoveOneItem}
        readonly={readonly}
        dataTest={'inventory-list'}
        hideTitle={hideTitle}
        handleChange={handleChange}
        onShowMoreClickHandler={() => {
          setDrawerState(true);
        }}
        customSubtitle={hideTitle ? undefined : inventoryListDisplayValue}
      />

      <Drawer isStackedDrawer onClose={handleCloseDrawer} open={drawerOpen}>
        <DrawerHeader onClose={handleCloseDrawer}>{t('Inventory List')}</DrawerHeader>

        <FiltersTitle
          sx={{ mb: 1, mt: 8 }}
          title={t('{type} [ {count} ]', {
            count: inventoryList?.values?.length || 0,
            type: listType === 'inventorylist.bundle' ? t('App Bundle ID') : t('Domains'),
          })}
          readonly={true}
          disabledReasonMessage={disabledReasonMessage}
        />

        <FlatList
          dataTest="transfer-list-bulk-list"
          data={listData}
          handleRowClick={noop}
          isSelectedList={false}
          isDisabled={false}
          hasBulkInput={false}
          hideActionIcon
          fullWidth
        />

        <DrawerActionBar
          actions={[
            {
              label: t('Close'),
              variant: 'text',
              onClick: handleCloseDrawer,
            },
          ]}
          stickyBottom
        />
      </Drawer>
    </Box>
  );
};
